// Shared queue call-out player.
//
// Every station polls the latest announcement and decides whether to play it:
//   TV (Queue Display) ....... every call
//   Reception PC ............. reception + cashier + clinic (doctor)
//   Cashier PC (POS) ......... cashier + clinic (doctor)
// A doctor's call therefore sounds everywhere, while reception/cashier calls
// stay at their own station (plus the TV).
//
// Browsers block autoplay until the user interacts, so each station arms audio
// once via enableAnnouncerSound() — the same one-tap the TV already uses.

import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

export const ACCEPT_ALL = ["reception", "cashier", "clinic", "doctor"];
export const ACCEPT_RECEPTION = ["reception", "cashier", "clinic", "doctor"];
export const ACCEPT_CASHIER = ["cashier", "clinic", "doctor"];

// Known female voices shipped by Windows / macOS / Chrome, so we can rule them
// out of the fallback pool rather than just hoping a male one matches first.
//
// "google us english" earns its place: it is female but its name carries no
// gender marker, so without it the last-resort fallback happily selects it.
const FEMALE_VOICE_NAMES = [
  "female", "samantha", "zira", "victoria", "karen", "moira", "tessa", "fiona",
  "susan", "hazel", "eva", "catherine", "linda", "heera", "kalpana", "serena",
  "allison", "ava", "nicky", "joanna", "salli", "kimberly", "amy", "emma",
  "google us english", "aria", "jenny", "michelle", "ana", "sonia", "libby",
  "natasha", "clara", "yan", "molly", "elsa", "isabella", "zoe", "shelley",
];

// Order matters — the first match wins. Microsoft David is the house voice: it
// reads Filipino patient names more clearly than the alternatives, so it is
// tried ahead of the generic "male" match (which would otherwise grab
// "Google UK English Male" on a machine that has both).
const MALE_VOICE_NAMES = [
  "microsoft david", "david",
  "male", "mark", "george", "daniel", "alex", "fred", "guy", "ryan",
  "james", "aaron", "arthur", "oliver", "rishi", "tom", "brian", "matthew",
  "google uk english male",
];

const isFemaleVoice = (v) => {
  const n = (v?.name || "").toLowerCase();
  // "male" is a substring of "female", so check female first.
  return FEMALE_VOICE_NAMES.some((f) => n.includes(f));
};

// Chrome populates the voice list asynchronously: getVoices() returns [] on the
// first call and only fills in once 'voiceschanged' fires. Picking a voice from
// that empty list silently leaves the utterance on the browser default — which
// on Windows Chrome is "Google US English", a female voice. So resolve the list
// once, up front, and hand it to the picker.
let voicesPromise = null;

export function loadVoices() {
  if (voicesPromise) return voicesPromise;
  voicesPromise = new Promise((resolve) => {
    const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
    if (!synth?.getVoices) return resolve([]);

    const now = synth.getVoices() || [];
    if (now.length) return resolve(now);

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve(synth.getVoices() || []);
    };
    try {
      synth.addEventListener("voiceschanged", done, { once: true });
    } catch {
      synth.onvoiceschanged = done;
    }
    setTimeout(done, 2000); // don't block a call-out forever
  });
  return voicesPromise;
}

// Which voices a browser exposes is device-specific, so name matching can only
// ever be a good default. A station whose machine has an odd voice set can pin
// an exact voice; the choice belongs to that device, hence localStorage.
const VOICE_PREF_KEY = "rx_callout_voice";

export function getVoicePreference() {
  try { return localStorage.getItem(VOICE_PREF_KEY) || ""; } catch { return ""; }
}
export function setVoicePreference(voiceURI) {
  try {
    if (voiceURI) localStorage.setItem(VOICE_PREF_KEY, voiceURI);
    else localStorage.removeItem(VOICE_PREF_KEY);
  } catch {}
}

/** English voices on this device, flagged so a human can choose sensibly. */
export async function listVoices() {
  const voices = await loadVoices();
  const en = voices.filter((v) => /^en/i.test(v.lang));
  return (en.length ? en : voices).map((v) => ({
    name: v.name,
    lang: v.lang,
    voiceURI: v.voiceURI,
    likelyFemale: isFemaleVoice(v),
  }));
}

/** Pick a male English voice when the browser falls back to speech synthesis. */
export function maleVoice(voiceList) {
  try {
    const voices = voiceList || window.speechSynthesis?.getVoices?.() || [];
    if (!voices.length) return null;
    const en = voices.filter((v) => /^en/i.test(v.lang));
    const pool = en.length ? en : voices;

    // An explicit choice on this device always wins.
    const pinned = getVoicePreference();
    if (pinned) {
      const v = pool.find((x) => x.voiceURI === pinned) || voices.find((x) => x.voiceURI === pinned);
      if (v) return v;
    }

    // Deliberately NO automatic fil-PH preference here: device Filipino voices
    // vary wildly, and a low-quality one spells unfamiliar words letter by
    // letter — reported from a live station within minutes of trying it. The
    // Filipino voice comes from the server mp3; this fallback stays with the
    // known-good English voices. A fil voice can still be pinned via the picker.
    for (const name of MALE_VOICE_NAMES) {
      const v = pool.find((x) => {
        const n = (x.name || "").toLowerCase();
        return n.includes(name) && !isFemaleVoice(x);
      });
      if (v) return v;
    }
    // No named match — take any voice that isn't a known female one. If every
    // voice on this device looks female, there is nothing better to return than
    // the default; the station can pin a voice in Profile → Call-out voice.
    return pool.find((v) => !isFemaleVoice(v)) || pool[0] || null;
  } catch {
    return null;
  }
}

export async function speakText(text) {
  try {
    if (!window.speechSynthesis || !text) return;
    const voices = await loadVoices();
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const v = maleVoice(voices);
    if (v) u.voice = v;
    u.rate = 0.9;
    u.pitch = 0.9;
    u.volume = 1;
    window.speechSynthesis.speak(u);
  } catch {}
}

/**
 * Speak a sample using one specific voice, resolving when it finishes. Used by
 * the voice picker to audition each voice — the caller needs the "finished"
 * signal to know when to move on to the next one.
 */
export function speakWithVoice(text, voiceURI) {
  return new Promise(async (resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(); } };
    try {
      if (!window.speechSynthesis || !text) return finish();
      const voices = await loadVoices();
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const v = voices.find((x) => x.voiceURI === voiceURI);
      if (v) u.voice = v;
      u.rate = 0.9;
      u.pitch = 0.9;
      u.volume = 1;
      u.onend = finish;
      u.onerror = finish;
      window.speechSynthesis.speak(u);
      setTimeout(finish, 15000);
    } catch { finish(); }
  });
}

/** Cut off anything currently being spoken (the picker's Stop button). */
export function stopSpeaking() {
  try { window.speechSynthesis?.cancel(); } catch {}
}

// Call-outs are announced twice in case the patient missed the first one. The
// repeat starts after the first has finished playing, plus this pause.
export const REPEAT_TYPES = ["reception", "cashier", "clinic", "doctor"];
export const REPEAT_TIMES = 2;
export const REPEAT_GAP_MS = 2500;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Speak once, resolving when the utterance actually ends (with a safety cap). */
function speakOnce(text) {
  return new Promise(async (resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(); } };
    try {
      if (!window.speechSynthesis || !text) return finish();
      const voices = await loadVoices();
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const v = maleVoice(voices);
      if (v) u.voice = v;
      u.rate = 0.9;
      u.pitch = 0.9;
      u.volume = 1;
      u.onend = finish;
      u.onerror = finish;
      window.speechSynthesis.speak(u);
      setTimeout(finish, 12000); // never hang the repeat loop
    } catch { finish(); }
  });
}

/** Play the server's TTS mp3 once, resolving when it ends; falls back to speech. */
async function playOnce(a) {
  if (a.audio_url) {
    try {
      // The audio endpoint requires the login token, and a bare <audio src>
      // request cannot send one — every station was silently getting 401 and
      // dropping to browser speech, so the server voice was never actually
      // heard. Fetch WITH the token, then play the blob.
      const res = await fetch(a.audio_url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rxp_token") || ""}` },
      });
      if (res.ok) {
        const blobUrl = URL.createObjectURL(await res.blob());
        const playedOk = await new Promise((resolve) => {
          let done = false;
          const finish = (ok) => {
            if (done) return;
            done = true;
            URL.revokeObjectURL(blobUrl);
            resolve(ok);
          };
          const audio = new Audio(blobUrl);
          audio.onended = () => finish(true);
          audio.onerror = () => finish(false);
          audio.play().catch(() => finish(false));
          setTimeout(() => finish(true), 20000);
        });
        if (playedOk) return;
        // Fetched but would not play (codec/corrupt) — still announce somehow.
      }
    } catch { /* fall through to speech */ }
  }
  await speakOnce(a.message || "");
}

// All playback on a station goes through one queue. Without it, a second
// announcement (double-click, poller race, repeat) starts on top of the first
// and the room hears two overlapping calls.
let _playChain = Promise.resolve();

/**
 * Play a call-out. The waiting-room TV repeats each call once (patients miss
 * the first one); interactive stations play once — a repeat at every station,
 * offset by polling, is what made one call sound like four.
 * Pass `{ repeat: 2 }` for the TV, `{ repeat: 1 }` (default) elsewhere.
 */
export function playAnnouncement(a, { repeat = 1 } = {}) {
  if (!a) return Promise.resolve();
  const run = async () => {
    for (let i = 0; i < repeat; i++) {
      if (i > 0) await sleep(REPEAT_GAP_MS);
      await playOnce(a);
    }
  };
  _playChain = _playChain.then(run, run);
  return _playChain;
}

/** Arm audio for this station (must run from a user gesture). */
export function enableAnnouncerSound() {
  try { window.speechSynthesis?.speak(new SpeechSynthesisUtterance(" ")); } catch {}
  loadVoices();   // warm the list so the first real call-out isn't voiceless
}

/**
 * Listen for queue call-outs and play the ones this station cares about.
 *
 * @param accept   call_type values to play (see ACCEPT_* above)
 * @param enabled  only polls/plays once the user has armed audio
 * Returns { markSelfPlayed } — call it with the announcement id you already
 * played locally so the poller doesn't play it a second time.
 */
export function useQueueAnnouncements({ accept = ACCEPT_ALL, enabled = true, intervalMs = 3000, repeat = 1 } = {}) {
  const playedRef = useRef(new Set());
  const primedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    // Calls this user made are played by the button handler itself; the poller
    // skipping them outright closes the race where a poll lands between the
    // server storing the announcement and the handler marking it self-played —
    // which played the same call twice, overlapping.
    let myUserId = "";
    try { myUserId = JSON.parse(localStorage.getItem("rxp_user") || "{}").id || ""; } catch {}

    const tick = async () => {
      try {
        const { data } = await api.get("/queue/announcements/latest");
        if (cancelled || !data?.id) return;

        // First poll after arming: adopt the current announcement without
        // replaying an old call-out.
        if (!primedRef.current) {
          primedRef.current = true;
          playedRef.current.add(data.id);
          return;
        }
        if (playedRef.current.has(data.id)) return;
        playedRef.current.add(data.id);

        if (myUserId && data.created_by === myUserId) return;
        const type = (data.call_type || "clinic").toLowerCase();
        if (!accept.includes(type)) return;
        await playAnnouncement(data, { repeat });
      } catch {}
    };

    tick();
    const t = setInterval(tick, intervalMs);
    return () => { cancelled = true; clearInterval(t); };
  }, [enabled, intervalMs, accept.join(","), repeat]);

  return {
    markSelfPlayed: (id) => { if (id) playedRef.current.add(id); },
  };
}

/** Small hook for the one-time "enable sound" toggle on a station. */
export function useSoundArm() {
  const [armed, setArmed] = useState(false);
  const arm = () => { enableAnnouncerSound(); setArmed(true); };
  return { armed, arm };
}
